/**
 * グローバル市場データを取得・購読するフック
 *
 * 使い方:
 * const { prices, indexes, news, loading, error, refetch } = useGlobalMarket();
 */

import { useState, useEffect, useCallback } from 'react';
import {
  supabase,
  MarketSnapshot,
  MarketNews,
  ChainNews,
  WeeklyTrend,
  SpotlightEvent,
  OvernightVolatility,
  NewsHistoryEntry,
  PersistentModifiers,
} from './supabase';

interface GlobalMarketState {
  prices: Record<string, number>;
  indexes: Record<string, number>;
  news: MarketNews[];
  chainNews: ChainNews[];
  newsHistory: NewsHistoryEntry[];
  activeTrend: WeeklyTrend | null;
  spotlightEvent: SpotlightEvent | null;
  suddenEvent: MarketNews | null;
  overnightVolatility: OvernightVolatility | null;
  persistentModifiers: PersistentModifiers | null;
  timestamp: Date | null;
  loading: boolean;
  error: string | null;
}

/** OHLC価格データ（統合履歴用） */
interface OHLCData {
  period: Date;
  open: number;
  close: number;
  high: number;
  low: number;
  granularity: '15min' | 'daily' | 'weekly';
}

interface UseGlobalMarketReturn extends GlobalMarketState {
  refetch: () => Promise<void>;
  getPrice: (cardId: string) => number;
  getPriceHistory: (cardId: string, hours?: number) => Promise<{ timestamp: Date; price: number }[]>;
  getUnifiedPriceHistory: (cardId: string, days?: number) => Promise<OHLCData[]>;
  getUnifiedIndexHistory: (indexKey: string, days?: number) => Promise<OHLCData[]>;
}

export function useGlobalMarket(): UseGlobalMarketReturn {
  const [state, setState] = useState<GlobalMarketState>({
    prices: {},
    indexes: {},
    news: [],
    chainNews: [],
    newsHistory: [],
    activeTrend: null,
    spotlightEvent: null,
    suddenEvent: null,
    overnightVolatility: null,
    persistentModifiers: null,
    timestamp: null,
    loading: true,
    error: null,
  });

  // 最新価格を取得
  const fetchLatestPrices = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const { data, error } = await supabase
        .from('global_market_snapshots')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        throw error;
      }

      if (data) {
        setState({
          prices: data.card_prices || {},
          indexes: data.indexes || {},
          news: data.news || [],
          chainNews: data.chain_news || [],
          newsHistory: data.news_history || [],
          activeTrend: data.active_trend || null,
          spotlightEvent: data.spotlight_event || null,
          suddenEvent: data.sudden_event || null,
          overnightVolatility: data.overnight_volatility || null,
          persistentModifiers: data.persistent_modifiers || null,
          timestamp: new Date(data.timestamp),
          loading: false,
          error: null,
        });
      }
    } catch (err) {
      console.error('Failed to fetch market prices:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to fetch prices',
      }));
    }
  }, []);

  // 特定カードの価格を取得
  const getPrice = useCallback((cardId: string): number => {
    return state.prices[cardId] || 0;
  }, [state.prices]);

  // 価格履歴を取得（チャート用）
  const getPriceHistory = useCallback(async (
    cardId: string,
    hours: number = 24
  ): Promise<{ timestamp: Date; price: number }[]> => {
    try {
      const { data, error } = await supabase
        .rpc('get_price_history', {
          p_card_id: cardId,
          p_hours: hours,
        });

      if (error) throw error;

      return (data || []).map((row: { timestamp: string; price: number }) => ({
        timestamp: new Date(row.timestamp),
        price: row.price,
      }));
    } catch (err) {
      console.error('Failed to fetch price history:', err);
      return [];
    }
  }, []);

  // 統合価格履歴を取得（3階層OHLC）
  const getUnifiedPriceHistory = useCallback(async (
    cardId: string,
    days: number = 90
  ): Promise<OHLCData[]> => {
    try {
      const { data, error } = await supabase
        .rpc('get_unified_price_history', {
          p_card_id: cardId,
          p_days: days,
        });

      if (error) throw error;

      return (data || []).map((row: {
        period: string;
        open_price: number;
        close_price: number;
        high_price: number;
        low_price: number;
        granularity: string;
      }) => ({
        period: new Date(row.period),
        open: row.open_price,
        close: row.close_price,
        high: row.high_price,
        low: row.low_price,
        granularity: row.granularity as '15min' | 'daily' | 'weekly',
      }));
    } catch (err) {
      console.error('Failed to fetch unified price history:', err);
      return [];
    }
  }, []);

  // 統合インデックス履歴を取得（3階層OHLC）
  const getUnifiedIndexHistory = useCallback(async (
    indexKey: string,
    days: number = 90
  ): Promise<OHLCData[]> => {
    try {
      const { data, error } = await supabase
        .rpc('get_unified_index_history', {
          p_index_key: indexKey,
          p_days: days,
        });

      if (error) throw error;

      return (data || []).map((row: {
        period: string;
        open_value: number;
        close_value: number;
        high_value: number;
        low_value: number;
        granularity: string;
      }) => ({
        period: new Date(row.period),
        open: row.open_value,
        close: row.close_value,
        high: row.high_value,
        low: row.low_value,
        granularity: row.granularity as '15min' | 'daily' | 'weekly',
      }));
    } catch (err) {
      console.error('Failed to fetch unified index history:', err);
      return [];
    }
  }, []);

  // 初回ロード
  useEffect(() => {
    fetchLatestPrices();
  }, [fetchLatestPrices]);

  // リアルタイム購読（新しいスナップショットが追加されたら自動更新）
  useEffect(() => {
    const subscription = supabase
      .channel('global_market_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'global_market_snapshots',
        },
        (payload: { new: MarketSnapshot }) => {
          const newData = payload.new;
          setState({
            prices: newData.card_prices || {},
            indexes: newData.indexes || {},
            news: newData.news || [],
            chainNews: newData.chain_news || [],
            newsHistory: newData.news_history || [],
            activeTrend: newData.active_trend || null,
            spotlightEvent: newData.spotlight_event || null,
            suddenEvent: newData.sudden_event || null,
            overnightVolatility: newData.overnight_volatility || null,
            persistentModifiers: newData.persistent_modifiers || null,
            timestamp: new Date(newData.timestamp),
            loading: false,
            error: null,
          });
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    ...state,
    refetch: fetchLatestPrices,
    getPrice,
    getPriceHistory,
    getUnifiedPriceHistory,
    getUnifiedIndexHistory,
  };
}

export default useGlobalMarket;
